'use client';

import type { ComponentProps, ReactNode } from 'react';
import { Suspense, useState, useEffect } from 'react';

import { NavMain } from '@/components/nav-main';
import { NavSecondary } from '@/components/nav-secondary';
// import { NavMatters } from '@/components/nav-matters'; // Temporarily hidden
import {
  QuickActionsTrigger,
  QuickActionsModal
} from '@/components/quick-actions';
import { CreateMatterSidebarTrigger } from '@/components/dashboard/create-matter-sidebar-trigger';
import { ImportMatterTrigger } from '@/components/integrations/pms/import-matter-trigger';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent
} from '@/components/ui/sidebar';
import {
  navigationData as defaultNavigationData,
  type NavigationData
} from './navigation-data';
import { useIsMiniBarVisible } from '@/lib/stores';
import { LawFirmHeader } from './law-firm-header';
import { SuggestionBoxButton } from '@/components/support';
import { GettingStartedWidget } from './getting-started-widget';
import { Button } from '../ui/button';
import { Headset } from 'lucide-react';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { cn } from '@/lib/utils';

interface AppSidebarProps extends ComponentProps<typeof Sidebar> {
  /**
   * Navigation data to display in the sidebar.
   * If not provided, uses default navigation data.
   */
  navigationData?: NavigationData;
  /**
   * Optional logo component to replace the default logo
   */
  logo?: ReactNode;
  /**
   * Optional brand name to display next to the logo
   */
  brandName?: string;
  /**
   * Whether the branding (logo and name) is loading
   */
  isLoadingBranding?: boolean;
}

/**
 * Inner content component for sidebar
 */
function AppSidebarContent({
  navigationData,
  logo,
  brandName,
  isLoadingBranding,
  searchOpen: _searchOpen,
  setSearchOpen
}: {
  navigationData: NavigationData;
  logo?: ReactNode;
  brandName: string;
  isLoadingBranding: boolean;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
}) {
  const isMiniBarVisible = useIsMiniBarVisible();
  const { hasFeature } = useFeatureFlags();
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  return (
    <>
      <SidebarHeader>
        <LawFirmHeader
          logo={logo}
          firmName={brandName}
          isLoading={isLoadingBranding}
        />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent className="flex flex-col gap-2">
            <CreateMatterSidebarTrigger onMenuOpenChange={setCreateMenuOpen} />
            <ImportMatterTrigger />
            <QuickActionsTrigger
              onClick={() => setSearchOpen(true)}
              className="w-full"
            />
          </SidebarGroupContent>
        </SidebarGroup>
        <div
          data-testid="sidebar-nav-items"
          className={cn(
            'flex min-h-0 flex-1 flex-col transition-opacity',
            createMenuOpen && 'opacity-40'
          )}
        >
          <Suspense fallback={null}>
            <NavMain items={navigationData.navAnchor} />
          </Suspense>
          {/* NavMatters temporarily hidden - includes matter list, search, and view all link
          <Suspense fallback={null}>
            <NavMatters />
          </Suspense>
          */}
          <NavSecondary
            items={navigationData.navSecondary}
            className="mt-auto"
          />
        </div>
      </SidebarContent>
      <SidebarFooter className={isMiniBarVisible ? 'pb-13.75' : ''}>
        <div className="space-y-2.5">
          <GettingStartedWidget />
          <SuggestionBoxButton />
          {hasFeature('messenger') && (
            <Button
              variant="outline"
              size="default"
              className="hover:bg-accent hover:text-accent-foreground w-full justify-center gap-2 px-3 text-sm font-normal"
              onClick={() => {
                type IntercomFn = (command: string, ...args: unknown[]) => void;
                const intercom = (window as { Intercom?: IntercomFn }).Intercom;
                if (typeof intercom === 'function') intercom('show');
              }}
            >
              <Headset className="h-4 w-4 shrink-0" aria-hidden="true" />
              Contact Support
            </Button>
          )}

          {/* <ContactValeButton /> */}
        </div>
      </SidebarFooter>
    </>
  );
}

/**
 * Application sidebar component
 * Provides main navigation, document links, and user menu
 *
 * @example
 * ```tsx
 * <AppSidebar
 *   navigationData={customNavData}
 *   brandName="My App"
 * />
 * ```
 */
export function AppSidebar({
  navigationData = defaultNavigationData,
  logo,
  brandName = 'Vale',
  isLoadingBranding = false,
  ...props
}: AppSidebarProps) {
  const [searchOpen, setSearchOpen] = useState(false);

  // Add keyboard shortcut for search (Cmd+K or Ctrl+K) - toggles modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev); // Toggle instead of always opening
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Ensure navigationData has all required properties
  const safeNavigationData = {
    ...defaultNavigationData,
    ...navigationData,
    navAnchor: navigationData?.navAnchor || defaultNavigationData.navAnchor,
    navSecondary:
      navigationData?.navSecondary || defaultNavigationData.navSecondary
  };

  return (
    <>
      <Sidebar variant="inset" {...props}>
        <AppSidebarContent
          navigationData={safeNavigationData}
          logo={logo}
          brandName={brandName}
          isLoadingBranding={isLoadingBranding}
          searchOpen={searchOpen}
          setSearchOpen={setSearchOpen}
        />
      </Sidebar>
      <QuickActionsModal open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
